import artwork from './wheel-artwork.json';

// Optical centres belong to an image, not to a frame/wheel/tyre combination.
// Explicit catalogue origins override these defaults; unfamiliar artwork falls
// back to the renderer's canvas centre until it has been measured.
export function wheelArtworkOrigin(image, origin, width, height) {
  const measured = artwork[(image || '').trim()];
  if (!measured || measured.width !== width || measured.height !== height) return { ...origin };
  return { x: origin.x == null ? measured.x : origin.x, y: origin.y == null ? measured.y : origin.y };
}

// Level the complete assembly, preserving every component's local attachment.
export function wheelLevel(buffer) {
  const frame = buffer.find(item => item.type === 'framesets');
  if (!frame || !frame.loaded) return null;
  const rear = buffer.find(item => item.type === 'wheelsLeft');
  const front = buffer.find(item => item.type === 'wheelsRight');
  if (!rear || !front || !(front.x > rear.x)) return null;
  return { x: (rear.x + front.x) / 2, y: (rear.y + front.y) / 2,
    angle: -Math.atan2(front.y - rear.y, front.x - rear.x) };
}

export function unlevelPoint(point, level) {
  if (!level) return point;
  const dx = point.x - level.x, dy = point.y - level.y;
  const c = Math.cos(level.angle), s = Math.sin(level.angle);
  return { x: level.x + dx*c + dy*s, y: level.y - dx*s + dy*c };
}

export function wheelArtworkRadius(image, width, height) {
  const measured = artwork[(image || '').trim()];
  return measured && measured.width === width && measured.height === height
    ? measured.radius : Math.max(width, height) / 2;
}

// Keep the full outer rings inside the backing canvas after axle levelling.
// Include the original viewport so fitting never crops the other components.
export function wheelViewportFit(buffer, width, height, padding = 24) {
  const level = wheelLevel(buffer);
  let left = 0, top = 0, right = width, bottom = height;
  const rings = buffer.filter(item => /^(wheels|tyres)(Left|Right)$/.test(item.type));
  if (!rings.length) return { x: 0, y: 0, scale: 1 };
  for (const item of rings) {
    const point = unlevelPoint(item, level && { ...level, angle: -level.angle });
    const radius = item.outerRadius * item.scale;
    if (!Number.isFinite(radius) || radius <= 0) continue;
    left = Math.min(left, point.x - radius);
    right = Math.max(right, point.x + radius);
    top = Math.min(top, point.y - radius);
    bottom = Math.max(bottom, point.y + radius);
  }
  const scale = Math.min(1, (width - padding * 2) / (right - left), (height - padding * 2) / (bottom - top));
  return { scale, x: (width - (right + left) * scale) / 2, y: (height - (bottom + top) * scale) / 2 };
}

export function unfitPoint(point, fit) {
  return { x: (point.x - fit.x) / fit.scale, y: (point.y - fit.y) / fit.scale };
}
